import React from 'react';
import { AuditAlert, AlertSeverity, AlertType } from '../../types/api';

interface AlertListProps {
  alerts: AuditAlert[];
  onResolve: (alertId: string) => void;
  resolving: boolean;
}

const AlertList: React.FC<AlertListProps> = ({ alerts, onResolve, resolving }) => {
  const formatAmount = (amount: string) => {
    // Convert from wei/gwei to ETH by dividing by 1e18
    const num = parseFloat(amount) / 1e18;
    if (num >= 1000000) return `${(num / 1000000).toFixed(2)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(2)}K`;
    return num.toFixed(6);
  };

  const getSeverityColor = (severity: AlertSeverity) => {
    switch (severity) {
      case AlertSeverity.CRITICAL:
        return 'bg-red-100 text-red-800 border-red-200';
      case AlertSeverity.ERROR:
        return 'bg-red-50 text-red-700 border-red-200';
      case AlertSeverity.WARNING:
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case AlertSeverity.INFO:
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getSeverityIcon = (severity: AlertSeverity) => {
    switch (severity) {
      case AlertSeverity.CRITICAL:
        return '🚨';
      case AlertSeverity.ERROR:
        return '❌';
      case AlertSeverity.WARNING:
        return '⚠️';
      case AlertSeverity.INFO:
        return 'ℹ️';
      default:
        return '📋';
    }
  };

  const getTypeLabel = (type: AlertType) => {
    return type.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
  };

  const getTimeAgo = (timestamp: number) => {
    const diff = Date.now() - timestamp;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return 'Just now';
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleString();
  };

  return (
    <div className="divide-y divide-gray-200">
      {alerts.length === 0 ? (
        <div className="px-6 py-12 text-center">
          <div className="text-green-400 mb-4">
            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No alerts found</h3>
          <p className="text-gray-500">System is running smoothly with no alerts to show.</p>
        </div>
      ) : (
        alerts.map((alert) => (
          <div key={alert.id} className={`px-6 py-6 ${alert.resolved ? 'bg-gray-50' : 'bg-white'} hover:bg-gray-50`}>
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-4 flex-1">
                <div className="flex-shrink-0">
                  <span className="text-2xl">{getSeverityIcon(alert.severity)}</span>
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getSeverityColor(alert.severity)}`}>
                      {alert.severity}
                    </span>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      {getTypeLabel(alert.type)}
                    </span>
                    {alert.resolved ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Resolved
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        Active
                      </span>
                    )}
                  </div>
                  
                  <h3 className={`text-lg font-medium mb-2 ${alert.resolved ? 'text-gray-600' : 'text-gray-900'}`}>
                    {alert.title}
                  </h3>
                  
                  <p className={`text-sm mb-3 ${alert.resolved ? 'text-gray-500' : 'text-gray-700'}`}>
                    {alert.description}
                  </p>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs text-gray-500">
                    <div>
                      <span className="font-medium">Created:</span>
                      <br />
                      {formatDate(alert.timestamp)}
                    </div>
                    
                    {alert.transactionId && (
                      <div>
                        <span className="font-medium">Transaction:</span>
                        <br />
                        <span className="font-mono">{alert.transactionId.slice(0, 8)}...</span>
                      </div>
                    )}
                    
                    {alert.chainId && (
                      <div>
                        <span className="font-medium">Chain:</span>
                        <br />
                        {(alert.chainId === 56 || alert.chainId === 97) ? 'BSC' : (alert.chainId === 2020 || alert.chainId === 2021) ? 'Ronin' : `Chain ${alert.chainId}`}
                      </div>
                    )}
                    
                    {alert.amount && (
                      <div>
                        <span className="font-medium">Amount:</span>
                        <br />
                        {formatAmount(alert.amount)} PFVS
                      </div>
                    )}
                    
                    {alert.resolved && alert.resolvedAt && (
                      <div>
                        <span className="font-medium">Resolved:</span>
                        <br />
                        {formatDate(alert.resolvedAt)}
                      </div>
                    )}
                    
                    {alert.resolved && alert.resolvedBy && (
                      <div>
                        <span className="font-medium">Resolved by:</span>
                        <br />
                        <span className="font-mono">{alert.resolvedBy.slice(0, 8)}...</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="flex-shrink-0 ml-4">
                <div className="flex flex-col items-end space-y-2">
                  <span className="text-xs text-gray-500">
                    {getTimeAgo(alert.timestamp)}
                  </span>
                  
                  {!alert.resolved && (
                    <button
                      onClick={() => onResolve(alert.id)}
                      disabled={resolving}
                      className={`inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white transition-colors ${
                        resolving
                          ? 'bg-gray-400 cursor-not-allowed'
                          : 'bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500'
                      }`}
                    >
                      {resolving ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-3 w-3 text-white" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Resolving...
                        </>
                      ) : (
                        'Mark Resolved'
                      )}
                    </button>
                  )}
                  
                  {alert.severity === AlertSeverity.CRITICAL && !alert.resolved && (
                    <div className="text-xs text-red-600 font-medium">
                      🔥 URGENT
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default AlertList; 